import { apiClient } from "./apiClient";

import type {
  CreateFilterOptionInput,
  CreateFilterValueInput,
  DeleteFilterOptionData,
  DeleteFilterValueData,
  FilterOptionDetailsData,
  FilterOptionItemData,
  FilterOptionsListData,
  FilterOptionsQuery,
  FilterValueDetailsData,
  FilterValueItemData,
  FilterValuesListData,
  FilterValuesQuery,
  UpdateFilterOptionInput,
  UpdateFilterValueInput,
} from "../types/filter-options.types";

function buildQuery(
  query:
    | FilterOptionsQuery
    | FilterValuesQuery = {},
): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) {
    params.set("search", query.search.trim());
  }

  if (query.isActive !== undefined) {
    params.set(
      "isActive",
      String(query.isActive),
    );
  }

  if ("includeValues" in query &&
      query.includeValues !== undefined) {
    params.set(
      "includeValues",
      String(query.includeValues),
    );
  }

  if (query.sort) {
    params.set("sort", query.sort);
  }

  if (query.cursor?.trim()) {
    params.set("cursor", query.cursor.trim());
  }

  if (query.limit !== undefined) {
    params.set("limit", String(query.limit));
  }

  const queryString = params.toString();

  return queryString
    ? `?${queryString}`
    : "";
}

export const filterOptionsApi = {
  listOptions(
    query: FilterOptionsQuery = {},
  ) {
    return apiClient.get<FilterOptionsListData>(
      `/filter-options${buildQuery(query)}`,
    );
  },

  getOptionById(optionId: string) {
    return apiClient.get<FilterOptionDetailsData>(
      `/filter-options/${encodeURIComponent(
        optionId,
      )}`,
    );
  },

  createOption(
    input: CreateFilterOptionInput,
  ) {
    return apiClient.post<FilterOptionItemData>(
      "/filter-options",
      input,
    );
  },

  updateOption(
    optionId: string,
    input: UpdateFilterOptionInput,
  ) {
    return apiClient.patch<FilterOptionItemData>(
      `/filter-options/${encodeURIComponent(
        optionId,
      )}`,
      input,
    );
  },

  deleteOption(optionId: string) {
    return apiClient.delete<DeleteFilterOptionData>(
      `/filter-options/${encodeURIComponent(
        optionId,
      )}`,
    );
  },

  listValues(
    optionId: string,
    query: FilterValuesQuery = {},
  ) {
    return apiClient.get<FilterValuesListData>(
      `/filter-options/${encodeURIComponent(
        optionId,
      )}/values${buildQuery(query)}`,
    );
  },

  getValueById(
    optionId: string,
    valueId: string,
  ) {
    return apiClient.get<FilterValueDetailsData>(
      `/filter-options/${encodeURIComponent(
        optionId,
      )}/values/${encodeURIComponent(
        valueId,
      )}`,
    );
  },

  createValue(
    optionId: string,
    input: CreateFilterValueInput,
  ) {
    return apiClient.post<FilterValueItemData>(
      `/filter-options/${encodeURIComponent(
        optionId,
      )}/values`,
      input,
    );
  },

  updateValue(
    optionId: string,
    valueId: string,
    input: UpdateFilterValueInput,
  ) {
    return apiClient.patch<FilterValueItemData>(
      `/filter-options/${encodeURIComponent(
        optionId,
      )}/values/${encodeURIComponent(
        valueId,
      )}`,
      input,
    );
  },

  deleteValue(
    optionId: string,
    valueId: string,
  ) {
    return apiClient.delete<DeleteFilterValueData>(
      `/filter-options/${encodeURIComponent(
        optionId,
      )}/values/${encodeURIComponent(
        valueId,
      )}`,
    );
  },
};